import type { Program } from "@coral-xyz/anchor";
import { LAMPORTS_PER_SOL, PublicKey } from "@solana/web3.js";
import { WRAPPED_SOL_MINT } from "@stepfinance/common";
import type { PendingStakingReward, StakingPosition } from "@stepfinance/staking";
import Decimal from "decimal.js";
import type { Vault } from "hedge-web3";
import { getHedgeMintPublicKey, getLiquidationPoolStatePublicKey, getPoolPublicKeyForMint } from "hedge-web3";

import type { Wallet } from "../../types/wallet";
import type { HedgeProgram } from ".";
import { HDG_MINT, HEDGE_PROGRAM_PUBLICKEY, hedgeRewardsDecay, u128ToDecimal, USH_MINT } from ".";

const hedgeStakingPoolProgramInterface = ({
  hedgeProgram,
  hedgeStakingPoolPublicKey,
}: {
  hedgeProgram: Program<Vault>;
  hedgeStakingPoolPublicKey: PublicKey;
}) => ({
  async fetchStakingPoolPosition(walletPublicKey: string) {
    return await hedgeProgram.account.stakingPoolPosition.all([
      // Filter on positions the user owns
      {
        memcmp: {
          bytes: walletPublicKey,
          offset: 8 + 16 * (128 * 2 + 2) + 8 * 4,
        },
      },
      // Filter on the hedge staking pool
      {
        memcmp: {
          bytes: hedgeStakingPoolPublicKey.toString(),
          offset: 8 + 16 * (128 * 2 + 2) + 8 * 4 + 32,
        },
      },
    ]);
  },
});

export const fetchHedgeStakingPosition = async ({
  hedgeProgram,
  wallet,
  priceInUSD,
}: {
  hedgeProgram: HedgeProgram;
  wallet: Wallet;
  priceInUSD: (mint: string) => number | null;
}): Promise<StakingPosition | undefined> => {
  const hedgeMintPublicKey = await getHedgeMintPublicKey(HEDGE_PROGRAM_PUBLICKEY);
  const [hedgeStakingPoolPublicKey] = await getPoolPublicKeyForMint(HEDGE_PROGRAM_PUBLICKEY, hedgeMintPublicKey);
  const hedgeStakingPool = await hedgeProgram.account.stakingPool.fetch(hedgeStakingPoolPublicKey);

  const hedgeInterface = hedgeStakingPoolProgramInterface({ hedgeProgram, hedgeStakingPoolPublicKey });
  const hedgeStakingPoolPositions = await hedgeInterface.fetchStakingPoolPosition(wallet.publicKey.toString());
  if (hedgeStakingPoolPositions.length > 0) {
    const ushPrice = priceInUSD(USH_MINT) ?? 0;
    const hdgPrice = priceInUSD(HDG_MINT) ?? 0;
    const hedgeStakingPosition = hedgeStakingPoolPositions[0].account;
    const ushFeeAccumulator = u128ToDecimal(hedgeStakingPool.ushFeeAccumulator);
    const startUshFeeAccumulator = u128ToDecimal(hedgeStakingPosition.startUshFeeAccumulator);
    const vaultInfos = await hedgeProgram.account.vaultType.all();
    const deposited = new Decimal(hedgeStakingPosition.deposited.toNumber());
    const ushRewards = ushFeeAccumulator
      .sub(startUshFeeAccumulator)
      .mul(new Decimal(deposited))
      .div(new Decimal(10 ** 9))
      .toNumber();
    const ushRewardsUSD = ushRewards * ushPrice;
    let solReward = 0;
    for (let i = 0; i < vaultInfos.length; i++) {
      const vaultInfo = vaultInfos[i].account;
      const vaultIndex = vaultInfo.collateralIndex.toNumber();
      const collateralFeeAccumulator = u128ToDecimal(hedgeStakingPool.collateralFeeAccumulator[vaultIndex]);
      const startCollateralFeeAccumulator = u128ToDecimal(
        hedgeStakingPosition.startCollateralFeeAccumulator[vaultIndex],
      );
      const rewardBalance = collateralFeeAccumulator
        .sub(startCollateralFeeAccumulator)
        .mul(deposited)
        .div(new Decimal(10 ** 9))
        .toNumber();
      if (vaultInfo.collateralMint.toString() === WRAPPED_SOL_MINT.toString()) {
        solReward += rewardBalance;
      }
    }
    const solPrice = priceInUSD(WRAPPED_SOL_MINT.toString()) ?? 0;
    const pendingRewardUSD = ushRewardsUSD + solReward * solPrice;
    const pendingRewardsList: PendingStakingReward[] = [];
    ushRewards > 0 &&
      pendingRewardsList.push({
        tokenMint: USH_MINT,
        tokenAmount: ushRewards,
      });
    solReward > 0 &&
      pendingRewardsList.push({
        tokenMint: WRAPPED_SOL_MINT.toString(),
        tokenAmount: solReward,
      });
    const amountStaked = deposited.toNumber() / LAMPORTS_PER_SOL;
    const valueInUSD = amountStaked * hdgPrice;

    // we fetch a special position to calculate APR to match Hedge UI
    const aprPositionResult = await hedgeInterface.fetchStakingPoolPosition(
      "7Q1YTwHAoXnB86MQzZ11BS3kqXVMBAgrBPFEioKJRRQU",
    );
    const aprPosition = aprPositionResult[0].account;
    const aprPositionStartUshFeeAccumulator = u128ToDecimal(aprPosition.startUshFeeAccumulator);
    const aprPositionDeposited = new Decimal(aprPosition.deposited.toNumber());
    const aprPositionUsh = ushFeeAccumulator
      .sub(aprPositionStartUshFeeAccumulator)
      .mul(aprPositionDeposited)
      .div(new Decimal(LAMPORTS_PER_SOL));
    let totalPoolRewardsUSD = aprPositionUsh.toNumber() * ushPrice;
    for (let i = 0; i < vaultInfos.length; i++) {
      const vaultInfo = vaultInfos[i].account;
      const vaultIndex = vaultInfo.collateralIndex.toNumber();
      const collateralFeeAccumulator = u128ToDecimal(hedgeStakingPool.collateralFeeAccumulator[vaultIndex]);
      const aprStartCollateralFeeAccumulator = u128ToDecimal(aprPosition.startCollateralFeeAccumulator[vaultIndex]);
      const rewardBalance = collateralFeeAccumulator
        .sub(aprStartCollateralFeeAccumulator)
        .mul(aprPositionDeposited)
        .div(new Decimal(LAMPORTS_PER_SOL))
        .toNumber();
      const rewardPriceUSD = priceInUSD(vaultInfo.collateralMint.toString()) ?? 0;
      const rewardValueUSD = rewardBalance * rewardPriceUSD;
      totalPoolRewardsUSD += rewardValueUSD;
    }
    const timePassed = Math.floor(Date.now() / 1000) - aprPosition.timestampOpened.toNumber();
    const rewardsPerYear = (totalPoolRewardsUSD / timePassed) * 60 * 60 * 24 * 365;
    const aprPositionStakedValueUSD = (aprPositionDeposited.toNumber() / LAMPORTS_PER_SOL) * hdgPrice;
    const apr = aprPositionStakedValueUSD > 0 ? rewardsPerYear / aprPositionStakedValueUSD : 0;

    return {
      kind: "Hedge",
      link: "https://www.hedge.so/pools",
      apy: apr,
      stakingMint: new PublicKey(HDG_MINT),
      amountStaked: deposited.toNumber() / 10 ** 9,
      pendingRewardsList,
      rewardMint: new PublicKey(USH_MINT),
      pendingReward: null,
      pendingRewardUSD,
      valueInUSD,
    };
  }
};

export const fetchHedgeStabilityPoolPosition = async ({
  hedgeProgram,
  wallet,
  priceInUSD,
}: {
  hedgeProgram: HedgeProgram;
  wallet: Wallet;
  priceInUSD: (mint: string) => number | null;
}): Promise<StakingPosition | undefined> => {
  const hedgeStabilityPoolPositions = await hedgeProgram.account.liquidationPosition.all([
    { memcmp: { bytes: wallet.publicKey.toString(), offset: 8 + 32 } },
  ]);
  if (hedgeStabilityPoolPositions.length > 0) {
    const ushPrice = priceInUSD(USH_MINT) ?? 0;
    const hdgPrice = priceInUSD(HDG_MINT) ?? 0;
    const hedgeStabilityPoolPosition = hedgeStabilityPoolPositions[0].account;
    const productSnapshotEntry = u128ToDecimal(hedgeStabilityPoolPosition.productSnapshotEntry.toString());
    const deposit = new Decimal(hedgeStabilityPoolPosition.deposit.toString());
    const poolStateKey = await getLiquidationPoolStatePublicKey(HEDGE_PROGRAM_PUBLICKEY);
    const stabilityPoolState = await hedgeProgram.account.liquidationPoolState.fetch(poolStateKey);
    const stabilityPoolEra = await hedgeProgram.account.liquidationPoolEra.fetch(hedgeStabilityPoolPosition.era);
    const pendingRewardsList: PendingStakingReward[] = [];
    let totalSolReward = 0;
    for (let i = 0; i < 2; i++) {
      const rewardEraSum = u128ToDecimal(stabilityPoolEra.sumBytes[i]);
      const rewardSumSnapshotsEntry = u128ToDecimal(hedgeStabilityPoolPosition.sumSnapshotsEntry[i]);
      totalSolReward += rewardEraSum
        .minus(rewardSumSnapshotsEntry)
        .div(productSnapshotEntry)
        .mul(deposit)
        .floor()
        .toNumber();
    }
    totalSolReward > 0 &&
      pendingRewardsList.push({
        tokenMint: WRAPPED_SOL_MINT.toString(),
        tokenAmount: totalSolReward / LAMPORTS_PER_SOL,
      });
    const hedgeInitRewardsTimestamp = stabilityPoolState.hedgeInitRewardsTimestamp.toNumber();
    const totalUshDeposits = new Decimal(stabilityPoolEra.totalDeposits.toString());
    const getHedgeRewardAmount = (): number => {
      const LiquidationPoolTotalSupply = 2000000.0 * LAMPORTS_PER_SOL;
      const hedgeRewardsSinceLastUpdate = hedgeRewardsDecay(
        LiquidationPoolTotalSupply,
        hedgeInitRewardsTimestamp * 1000,
        stabilityPoolEra.hedgeRewardsTimestamp.toNumber() * 1000,
        Date.now(),
        365 * 1000,
      );
      if (totalUshDeposits.isZero()) {
        return 0;
      }
      const stabilityPoolEraProduct = u128ToDecimal(stabilityPoolEra.productBytes.toString());
      const rewardsPerToken = stabilityPoolEraProduct.mul(
        new Decimal(hedgeRewardsSinceLastUpdate).div(totalUshDeposits),
      );
      const newAccumulator = rewardsPerToken.add(
        new Decimal(u128ToDecimal(stabilityPoolEra.hedgeRewardsAccumulatorBytes.toString())),
      );
      const hedgeAvailable = newAccumulator.minus(
        u128ToDecimal(hedgeStabilityPoolPosition.hedgeRewardsSnapshotAccum.toString()),
      );
      return hedgeAvailable.mul(new Decimal(deposit)).div(productSnapshotEntry).toNumber();
    };
    const hedgeRewardAmount = getHedgeRewardAmount();
    hedgeRewardAmount > 0 &&
      pendingRewardsList.push({
        tokenMint: HDG_MINT,
        tokenAmount: hedgeRewardAmount / LAMPORTS_PER_SOL,
      });
    const pendingRewardUSD = pendingRewardsList.reduce(
      (accum, reward) => (accum += reward.tokenAmount * (priceInUSD(reward.tokenMint.toString()) ?? 0)),
      0,
    );

    // calculate APR
    const currentRewardsPerDay =
      hedgeRewardsDecay(
        2000000.0 * LAMPORTS_PER_SOL,
        hedgeInitRewardsTimestamp * 1000,
        Date.now(),
        Date.now() + 24 * 60 * 60 * 1000,
        365 * 1000,
      ) / LAMPORTS_PER_SOL;
    let apr = new Decimal(0);
    if (totalUshDeposits.greaterThan(0) && currentRewardsPerDay > 0 && ushPrice) {
      const totalUshDepositsValue = totalUshDeposits.div(LAMPORTS_PER_SOL).mul(ushPrice);
      apr = new Decimal(currentRewardsPerDay * 365 * hdgPrice).div(totalUshDepositsValue);
    }
    const eraProduct = u128ToDecimal(stabilityPoolEra.productBytes.toString());
    const stakedValue = eraProduct.div(productSnapshotEntry).mul(deposit).floor().toNumber() / LAMPORTS_PER_SOL;

    return {
      kind: "Hedge",
      link: "https://www.hedge.so/pools",
      apy: apr.toNumber(),
      stakingMint: new PublicKey(USH_MINT),
      amountStaked: stakedValue,
      pendingRewardsList,
      rewardMint: new PublicKey(HDG_MINT),
      pendingReward: null,
      pendingRewardUSD,
      valueInUSD: stakedValue * ushPrice,
    };
  }
};
